import { z } from 'zod'
import { taskEventTypeSchema } from './events'

/**
 * Nachrichten über WebSocket (Konzept Kap. 8.1, Leitplanke 2).
 *
 * Der Aufgabenbestand liegt komplett im Client; diese Nachrichten halten ihn
 * aktuell. Zieht jemand eine Aufgabe, verschwindet sie bei allen anderen
 * sofort – ohne Neuladen und ohne Abfrage.
 *
 * Absichtlich schlank: die Nachricht sagt *was* sich geändert hat, der Client
 * entscheidet, ob er den Datensatz mitgeschickt bekommt oder nachlädt.
 */

export const serverMessageSchema = z.discriminatedUnion('typ', [
  /** Begrüßung nach dem Verbindungsaufbau – trägt die Serverversion. */
  z.object({
    typ: z.literal('willkommen'),
    serverZeit: z.string(),
    version: z.string(),
  }),
  /** Eine Aufgabe hat sich geändert oder ist neu. */
  z.object({
    typ: z.literal('aufgabe.geaendert'),
    aufgabeId: z.string(),
    /**
     * **In welcher Arbeitswelt** ([Z-07.9](../../../docs/phasen/zusatz-07-workspaces.md)).
     *
     * **Pflichtfeld, nicht optional** – und das ist der ganze Punkt. Ein `?` hier wäre der
     * bequeme Weg gewesen und genau der falsche: der Hub verteilt eine Nachricht ohne
     * Workspace an **alle**, und die Hälfte der einunddreißig Absender hätte das Feld
     * vergessen. Eine Grenze, die nur dort gilt, wo jemand daran gedacht hat, ist keine.
     *
     * Weil das Feld Pflicht ist, geht der Weg über {@link aufgabeGeaendert} im Server – eine
     * Stelle, die den Workspace selbst nachschlägt. Man kann sie nicht falsch aufrufen.
     */
    workspaceId: z.string(),
    /** Wer die Änderung ausgelöst hat – der Auslöser kann sein Echo ignorieren. */
    ausgeloestVon: z.string().nullable(),
  }),
  /** Eine Aufgabe wurde gelöscht oder zusammengeführt. */
  z.object({
    typ: z.literal('aufgabe.entfernt'),
    aufgabeId: z.string(),
    /**
     * Wie oben – und hier **muss** er vor dem Löschen gelesen werden: danach gibt es die Zeile
     * nicht mehr, aus der er käme.
     */
    workspaceId: z.string(),
  }),
  /** Neues Ereignis im Log – hält Leitstand und Zeitleiste aktuell (Phase 2). */
  z.object({
    typ: z.literal('ereignis.neu'),
    aufgabeId: z.string(),
    workspaceId: z.string(),
    ereignisTyp: taskEventTypeSchema,
    personId: z.string().nullable(),
  }),
  /** Sync-Zustand einer Quelle geändert – für die Admin-Sicht (Phase 1). */
  z.object({
    typ: z.literal('sync.geaendert'),
    quelleId: z.string(),
  }),
  /**
   * **Ein Dokument der Ablage hat sich geändert** – neu, neue Fassung, umbenannt, verschoben
   * oder gelöscht ([Z-14](../../../docs/phasen/zusatz-14-ablage.md)). Der Client wirft seinen
   * Baum weg und holt ihn neu; die Nachricht trägt bewusst nicht, *was* sich geändert hat.
   *
   * `workspaceId` ist Pflicht, aus demselben Grund wie oben: der Hub verteilt danach.
   */
  z.object({
    typ: z.literal('dokument.geaendert'),
    dokumentId: z.string(),
    workspaceId: z.string(),
    ausgeloestVon: z.string().nullable(),
  }),
])

export type ServerMessage = z.infer<typeof serverMessageSchema>

export const clientMessageSchema = z.discriminatedUnion('typ', [
  /** Lebenszeichen; der Server antwortet mit 'pong'. */
  z.object({ typ: z.literal('ping') }),
])

export type ClientMessage = z.infer<typeof clientMessageSchema>

/** Pfad des WebSocket-Endpunkts. Eine Wahrheit für Server und Client. */
export const REALTIME_PATH = '/realtime'
